import React, { useEffect, useMemo, useRef, useState } from "react";
import { countryCodes } from "../../constant/CountryCode";

interface CountryCodeFlagDropdownProps {
  value: string;
  onChange: (phone: string) => void;
}

const CountryCodeFlagDropdown: React.FC<CountryCodeFlagDropdownProps> = ({
  value,
  onChange,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const normalizedValue =
    value && value.trim().length > 0
      ? value.startsWith("+")
        ? value
        : `+${value}`
      : "";

  const selected = useMemo(() => {
    if (!normalizedValue) return countryCodes[0];
    return (
      countryCodes.find(
        (c) => c.dial_code.toLowerCase() === normalizedValue.toLowerCase()
      ) || countryCodes[0]
    );
  }, [normalizedValue]);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return countryCodes;
    const query = search.trim().toLowerCase();
    return countryCodes.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query) ||
        country.dial_code.includes(query)
    );
  }, [search]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const getFlagEmoji = (code?: string) => {
    if (!code) return "🌐";
    return code
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt(0))
      );
  };

  const handleSelect = (dialCode: string) => {
    onChange(dialCode);
    setOpen(false);
  };

  return (
    <div className="country-pill" ref={wrapperRef}>
      <button
        type="button"
        className={`country-pill__trigger ${open ? "is-active" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="country-pill__flag">
          {getFlagEmoji(selected?.code)}
        </span>
        <span className="country-pill__dial">{selected?.dial_code}</span>
        <span
          className={`country-pill__chevron ${open ? "rotate" : ""}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {open && (
        <div className="country-pill__menu card-glow">
          <div className="country-pill__search">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="16.65" y1="16.65" x2="21" y2="21" />
            </svg>
            <input
              type="text"
              placeholder="Search country or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="country-pill__list" role="listbox">
            {filteredCountries.map((country) => (
              <button
                type="button"
                key={country.code}
                className="country-pill__option"
                onClick={() => handleSelect(country.dial_code)}
              >
                <span className="country-pill__flag">
                  {getFlagEmoji(country.code)}
                </span>
                <span className="country-pill__option-name">
                  {country.name}
                </span>
                <span className="country-pill__option-code">
                  {country.dial_code}
                </span>
              </button>
            ))}
            {filteredCountries.length === 0 && (
              <div className="country-pill__empty">No matches found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeFlagDropdown;
